import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "./useAuth";
import {
	loadPendingTasks,
	loadPostedWorkSessionSuppressions,
	persistPostedWorkSessionSuppressions,
} from "../util/pendingTaskStorage";
import {
	WorkSessionDraft,
	WorkType,
	applyWorkSessionDraft,
	leaseIdToRelease,
	lockHeldByOther,
	mergeWorkSessionTasks,
	reconcilePostedWorkSessionSuppressions,
	serializeWorkSessionTask,
	taskNeedsPush,
} from "../util/workSessionSync";

type TaskLike = {
	id: string;
	is_public?: boolean;
	server_v?: number;
	owner_id?: number;
	locked_by?: number | null;
	active?: boolean;
};

const PUSH_DELAY_MS = 900;
const RETRY_MS = 4000;
const POLL_MS = 5000;

export const usePendingWorkSessions = <T extends TaskLike>({
	workType,
	enabled,
	tasks,
	setTasks,
	reviveTask,
	storageKey,
	extraTasksRef,
	belongsHere,
}: {
	workType: WorkType;
	enabled: boolean;
	tasks: T[];
	setTasks: Dispatch<SetStateAction<T[]>>;
	reviveTask: (raw: any) => T;
	storageKey: string;
	extraTasksRef?: MutableRefObject<T[]>;
	belongsHere?: (task: T) => boolean;
}) => {
	const { api, session } = useAuth();
	const { t } = useTranslation();
	const userId = Number(session?.user_id || 0);
	const hydratedRef = useRef(false);
	const lastPushedRef = useRef(new Map<string, string>());
	const failedIdsRef = useRef(new Set<string>());
	const inFlightRef = useRef(new Set<string>());
	const queuedIdsRef = useRef(new Set<string>());
	const lastKnownVRef = useRef(new Map<string, number>());
	const heldIdRef = useRef<string | null>(null);
	const tasksRef = useRef(tasks);
	tasksRef.current = tasks;

	const allTasks = (): T[] => [...tasksRef.current, ...(extraTasksRef?.current || [])];

	const versionOf = (task: T) =>
		Number(lastKnownVRef.current.get(task.id) ?? task.server_v ?? 0);

	const fingerprintOf = (task: T) =>
		JSON.stringify({
			work_type: workType,
			data: serializeWorkSessionTask(task as Record<string, any>),
			is_public: Boolean(task.is_public),
			server_v: versionOf(task),
		});

	const isDirty = (id: string) => {
		if (failedIdsRef.current.has(id)) return true;
		const task = allTasks().find((row) => row.id === id);
		if (!task) return false;
		const last = lastPushedRef.current.get(id);
		if (last === undefined) return false;
		return last !== fingerprintOf(task);
	};

	const needsPush = (task: T) =>
		!lockHeldByOther(task, userId) &&
		taskNeedsPush(
			lastPushedRef.current.get(task.id),
			fingerprintOf(task),
			failedIdsRef.current.has(task.id),
		);

	const applyMeta = (id: string, draft: WorkSessionDraft) => {
		const patch = (task: T): T =>
			task.id === id
				? {
					...task,
					server_v: draft.server_v,
					owner_id: draft.owner_id,
					owner_name: draft.owner_name,
					is_public: draft.is_public,
					locked_by: draft.locked_by,
					locked_by_name: draft.locked_by_name,
					locked_until: draft.locked_until,
				} as T
				: task;
		setTasks((prev) => prev.map(patch));
		if (extraTasksRef) {
			extraTasksRef.current = extraTasksRef.current.map(patch);
		}
	};

	const applyMerged = (merged: T[], dropIds: string[], uploadIds: string[]) => {
		const activeId = tasksRef.current.find((row) => row.active)?.id;
		const withActive = merged.map((row) => ({
			...row,
			active: activeId ? row.id === activeId : row.active,
		}));
		if (belongsHere && extraTasksRef) {
			extraTasksRef.current = withActive.filter((row) => !belongsHere(row));
			setTasks(withActive.filter((row) => belongsHere(row)));
		} else {
			setTasks(withActive);
		}
		dropIds.forEach((id) => {
			lastPushedRef.current.delete(id);
			failedIdsRef.current.delete(id);
			queuedIdsRef.current.delete(id);
			lastKnownVRef.current.delete(id);
			if (heldIdRef.current === id) heldIdRef.current = null;
		});
		merged.forEach((task) => {
			if (!uploadIds.includes(task.id)) {
				lastKnownVRef.current.set(task.id, Number(task.server_v || 0));
				lastPushedRef.current.set(task.id, fingerprintOf(task));
				failedIdsRef.current.delete(task.id);
			}
		});
		uploadIds.forEach((id) => {
			const task = merged.find((row) => row.id === id);
			if (task) void pushTask(task);
		});
	};

	const pushTask = async (task: T): Promise<boolean> => {
		if (lockHeldByOther(task, userId)) {
			failedIdsRef.current.delete(task.id);
			return false;
		}
		const body = {
			work_type: workType,
			data: serializeWorkSessionTask(task as Record<string, any>),
			is_public: Boolean(task.is_public),
			server_v: versionOf(task),
		};
		const fingerprint = JSON.stringify(body);
		if (lastPushedRef.current.get(task.id) === fingerprint) {
			failedIdsRef.current.delete(task.id);
			return true;
		}
		if (inFlightRef.current.has(task.id)) {
			queuedIdsRef.current.add(task.id);
			return false;
		}

		inFlightRef.current.add(task.id);
		try {
			const res = await api.put(`/work_sessions/${task.id}`, body, { skipErrorToast: true });
			if (res?.status === 200 && res.data?.draft) {
				const draft = res.data.draft as WorkSessionDraft;
				lastKnownVRef.current.set(task.id, Number(draft.server_v || 0));
				lastPushedRef.current.set(
					task.id,
					JSON.stringify({
						...body,
						server_v: draft.server_v,
						is_public: draft.is_public,
					}),
				);
				failedIdsRef.current.delete(task.id);
				applyMeta(task.id, draft);
				if (draft.locked_by && Number(draft.locked_by) === userId) {
					heldIdRef.current = task.id;
				}
				return true;
			}
			if (res?.status === 409 && res.data?.draft) {
				failedIdsRef.current.delete(task.id);
				const draft = res.data.draft as WorkSessionDraft;
				lastKnownVRef.current.set(task.id, Number(draft.server_v || 0));
				const incoming = applyWorkSessionDraft(draft, reviveTask);
				if (lockHeldByOther(draft, userId)) {
					applyMeta(task.id, draft);
					toast.error(res.data?.message || t("common:workSessions.lockedByOther"), {
						toastId: `work-session-locked-${task.id}`,
						autoClose: 4000,
					});
					return false;
				}
				const latest = allTasks().find((row) => row.id === task.id) || task;
				const aligned = {
					...latest,
					server_v: draft.server_v,
					is_public: draft.is_public,
					active: latest.active,
				} as T;
				if (fingerprintOf(aligned) === fingerprintOf({ ...incoming, active: latest.active } as T)) {
					lastPushedRef.current.set(task.id, fingerprintOf(aligned));
					applyMeta(task.id, draft);
					return true;
				}
				// Stale version of our own write: keep local edits and retry at the
				// live server_v instead of clobbering keystrokes and toasting.
				applyMeta(task.id, draft);
				queuedIdsRef.current.add(task.id);
				return false;
			}
			if (res?.status === 401 || res?.status === 403) {
				failedIdsRef.current.delete(task.id);
				// Record the fingerprint so this exact body is never re-sent. Without
				// it the task stayed permanently "unpushed": every change to `tasks`
				// -- including the one the 5s poll triggers by itself -- fired another
				// PUT, another 403 and another toast, which is what made the
				// "You cannot open that unfinished task." message flood the screen.
				// A refused push is settled, not pending; only transient failures
				// belong in failedIdsRef for the retry timer to pick up.
				lastPushedRef.current.set(task.id, fingerprint);
				if (res?.status === 403 && res.data?.message) {
					toast.error(String(res.data.message), {
						toastId: `work-session-forbidden-${task.id}`,
						autoClose: 4000,
					});
				}
				return false;
			}
			failedIdsRef.current.add(task.id);
			return false;
		} finally {
			inFlightRef.current.delete(task.id);
			if (queuedIdsRef.current.has(task.id)) {
				queuedIdsRef.current.delete(task.id);
				const latest = allTasks().find((row) => row.id === task.id);
				if (latest && needsPush(latest)) {
					void pushTask(latest);
				}
			}
		}
	};

	/** Drop a draft remotely when authorized, and hide posted drafts locally. */
	const removeRemote = async (
		id: string,
		serverV?: number,
		{ posted = false }: { posted?: boolean } = {},
	) => {
		lastPushedRef.current.delete(id);
		failedIdsRef.current.delete(id);
		queuedIdsRef.current.delete(id);
		lastKnownVRef.current.delete(id);
		const suppressions = loadPostedWorkSessionSuppressions(storageKey, userId);
		if (posted) {
			suppressions.add(id);
			persistPostedWorkSessionSuppressions(storageKey, userId, suppressions);
		}
		if (!serverV) return;
		await api.delete(`/work_sessions/${id}`, { skipErrorToast: true });
	};

	const claimTask = async (id: string) => {
		const current = allTasks().find((row) => row.id === id);
		if (current && lockHeldByOther(current, userId)) {
			return;
		}
		if (heldIdRef.current === id || (userId > 0 && Number(current?.locked_by) === userId)) {
			return;
		}
		const res = await api.post(`/work_sessions/${id}/claim`, null, { skipErrorToast: true });
		if (res?.status === 200 && res.data?.draft) {
			applyMeta(id, res.data.draft as WorkSessionDraft);
			heldIdRef.current = id;
			return res;
		}
		if (res?.status === 409) {
			if (res.data?.draft) applyMeta(id, res.data.draft as WorkSessionDraft);
			toast.error(res.data?.message || t("common:workSessions.lockedByOther"), {
				toastId: `work-session-locked-${id}`,
				autoClose: 4000,
			});
		}
		return res;
	};

	const releaseTask = async (id: string) => {
		const task = allTasks().find((row) => row.id === id);
		const weHold = heldIdRef.current === id || (userId > 0 && Number(task?.locked_by) === userId);
		if (!weHold) return;
		if (heldIdRef.current === id) heldIdRef.current = null;
		const res = await api.post(`/work_sessions/${id}/release`, null, { skipErrorToast: true });
		if (res?.status === 200 && res.data?.draft) {
			applyMeta(id, res.data.draft as WorkSessionDraft);
			return;
		}
		applyMeta(id, {
			batch: id,
			work_type: workType,
			owner_id: Number(task?.owner_id || userId),
			is_public: Boolean(task?.is_public),
			server_v: Number(task?.server_v || 0),
			locked_by: null,
			locked_by_name: "",
			locked_until: null,
			data: {},
		});
	};

	const setPublic = async (task: T, isPublic: boolean) => {
		const next = { ...task, is_public: isPublic } as T;
		setTasks((prev) => prev.map((row) => (row.id === task.id ? next : row)));
		await pushTask(next);
	};

	const pullRemote = async () => {
		const res = await api.get("/work_sessions", { params: { work_type: workType } });
		if (res?.status !== 200) return false;
		const remote: WorkSessionDraft[] = res.data?.drafts || [];
		const memory = allTasks();
		const stored = memory.length > 0 ? memory : (loadPendingTasks(storageKey, reviveTask, userId) || []);
		const reconciled = reconcilePostedWorkSessionSuppressions(
			stored,
			remote,
			loadPostedWorkSessionSuppressions(storageKey, userId),
		);
		persistPostedWorkSessionSuppressions(storageKey, userId, reconciled.suppressedIds);
		const { tasks: merged, uploadIds, dropIds } = mergeWorkSessionTasks(
			reconciled.local,
			reconciled.remote,
			reviveTask,
			isDirty,
			userId,
		);
		applyMerged(merged, dropIds, uploadIds);
		return true;
	};

	useEffect(() => {
		if (!enabled || userId < 1) {
			hydratedRef.current = false;
			return;
		}

		let cancelled = false;
		(async () => {
			const ok = await pullRemote();
			if (cancelled) return;
			hydratedRef.current = true;
			if (!ok) return;
		})();

		return () => {
			cancelled = true;
			const heldId = heldIdRef.current;
			if (heldId) {
				heldIdRef.current = null;
				void api.post(`/work_sessions/${heldId}/release`, null, { skipErrorToast: true });
			}
		};
		// Hydrate once per open session / user.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, workType, userId]);

	useEffect(() => {
		if (!enabled || !hydratedRef.current) return;
		const handle = window.setTimeout(() => {
			allTasks().forEach((task) => {
				if (needsPush(task)) void pushTask(task);
			});
		}, PUSH_DELAY_MS);
		return () => window.clearTimeout(handle);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tasks, enabled]);

	useEffect(() => {
		if (!enabled || userId < 1) return;
		const retry = window.setInterval(() => {
			if (!hydratedRef.current) return;
			allTasks().forEach((task) => {
				if (failedIdsRef.current.has(task.id)) void pushTask(task);
			});
		}, RETRY_MS);
		return () => window.clearInterval(retry);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, workType, userId]);

	useEffect(() => {
		if (!enabled || userId < 1) return;
		const poll = window.setInterval(() => {
			if (!hydratedRef.current) return;
			if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
			void pullRemote();
		}, POLL_MS);
		return () => window.clearInterval(poll);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, workType, userId]);

	useEffect(() => {
		const active = tasks.find((row) => row.active);
		const toRelease = leaseIdToRelease(heldIdRef.current, active?.id, enabled);
		if (toRelease) {
			void releaseTask(toRelease);
		}
		if (!enabled || !hydratedRef.current) return;
		if (active?.is_public && !lockHeldByOther(active, userId)) {
			void claimTask(active.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tasks.find((row) => row.active)?.id, enabled]);

	return {
		userId,
		removeRemote,
		setPublic,
		claimTask,
		releaseTask,
	};
};
